import type { Borders, Worksheet } from 'exceljs'
import type { SpecMachine } from '../../types'
import type { Draft } from './specDoc'
import { tagLookup } from './masterView'

// 마스터데이터 내려받기 — 공유용 "수집 데이터" 표 ({명세서}_수집데이터.xlsx).
//   인터페이스 ID | 데이터 항목 | 전송코드
// 데이터 항목 = 항목 설명 (비었으면 연결한 태그의 설명). 한 시트에 모두(인터페이스 ID 칸을 세로로 합침) 또는 인터페이스마다 시트.
// 꾸밈은 차분하게 — 제목·부제, 옅은 회색 머리줄, 얇은 테두리, 인쇄는 가로 한 장 맞춤 + 머리줄 반복.
// exceljs 는 무거워 동적 import

export type ExportLayout = 'single' | 'perInterface'

const FONT = '맑은 고딕'
const LINE = { style: 'thin' as const, color: { argb: 'FFD0D5DD' } }
const BORDER: Partial<Borders> = { top: LINE, left: LINE, bottom: LINE, right: LINE }
const HEAD_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF2F4F7' } }
const ID_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFAFBFC' } }
const HEAD_ROW = 4

type Row = { id: string; item: string; code: string }

function rowsOf(m: SpecMachine, lookup: ReturnType<typeof tagLookup>): Row[] {
  const id = m.code.trim() || '(인터페이스 ID 없음)'
  if (m.items.length === 0) return [{ id, item: '', code: '' }]
  return m.items.map(i => ({
    id,
    item: i.description.trim() || lookup(i.device, i.address)?.tag.description.trim() || '',
    code: i.code.trim(),
  }))
}

// 시트 하나 — 제목(1), 부제(2), 빈 줄(3), 머리줄(4), 데이터(5~). groups = 인터페이스별 행 묶음 (ID 칸을 합친다)
function fillSheet(ws: Worksheet, title: string, subtitle: string, groups: Row[][]) {
  ws.columns = [{ width: 18 }, { width: 46 }, { width: 26 }]
  ws.properties.defaultRowHeight = 20

  ws.mergeCells('A1:C1')
  const t = ws.getCell('A1')
  t.value = title
  t.font = { name: FONT, size: 14, bold: true, color: { argb: 'FF1F2937' } }
  t.alignment = { vertical: 'middle' }
  ws.getRow(1).height = 28

  ws.mergeCells('A2:C2')
  const s = ws.getCell('A2')
  s.value = subtitle
  s.font = { name: FONT, size: 9, color: { argb: 'FF6B7280' } }
  ws.getRow(3).height = 8

  const head = ws.getRow(HEAD_ROW)
  head.values = ['인터페이스 ID', '데이터 항목', '전송코드']
  head.height = 24
  head.eachCell(c => {
    c.font = { name: FONT, size: 10, bold: true, color: { argb: 'FF374151' } }
    c.fill = HEAD_FILL
    c.alignment = { vertical: 'middle', horizontal: 'center' }
    c.border = { ...BORDER, bottom: { style: 'medium', color: { argb: 'FF9CA3AF' } } }
  })

  let r = HEAD_ROW + 1
  for (const group of groups) {
    const first = r
    for (const row of group) {
      const line = ws.getRow(r)
      line.values = [row.id, row.item, row.code]
      line.height = 20
      line.eachCell({ includeEmpty: true }, (c, col) => {
        c.font = { name: col === 3 ? 'Consolas' : FONT, size: 10, color: { argb: 'FF1F2937' } }
        c.border = BORDER
        c.alignment = { vertical: 'middle', horizontal: col === 2 ? 'left' : 'center', indent: col === 2 ? 1 : 0 }
      })
      r++
    }
    // 같은 인터페이스는 ID 칸을 하나로
    if (r - first > 1) ws.mergeCells(first, 1, r - 1, 1)
    const id = ws.getCell(first, 1)
    id.font = { name: FONT, size: 10, bold: true, color: { argb: 'FF1F2937' } }
    id.fill = ID_FILL
  }

  ws.views = [{ state: 'frozen', ySplit: HEAD_ROW, showGridLines: false }]
  ws.pageSetup = {
    paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
    margins: { left: 0.5, right: 0.5, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
    printTitlesRow: `${HEAD_ROW}:${HEAD_ROW}`,
  }
  ws.headerFooter = { oddFooter: '&C&8&P / &N' }
}

// 시트 이름 — 엑셀이 막는 글자 빼고 31자, 겹치면 (2)
function sheetName(raw: string, used: Set<string>) {
  const base = (raw.replace(/[\\/?*[\]:]/g, '_').trim() || '인터페이스').slice(0, 28)
  let name = base, n = 2
  while (used.has(name.toLowerCase())) name = `${base}(${n++})`
  used.add(name.toLowerCase())
  return name
}

export async function exportMasterXlsx(draft: Draft, layout: ExportLayout) {
  const { Workbook } = await import('exceljs')
  const wb = new Workbook()
  wb.creator = 'EdgeManager'
  const lookup = tagLookup(draft.devices)
  // 인터페이스 ID 도 항목도 없는 빈 행은 뺀다
  const machines = draft.machines.filter(m => m.code.trim() || m.items.length > 0)
  const date = new Date().toLocaleDateString('ko-KR')
  const specName = draft.name || '명세서'
  const total = machines.reduce((n, m) => n + m.items.length, 0)

  if (layout === 'single') {
    fillSheet(wb.addWorksheet('수집 데이터'), '수집 데이터',
      `${specName}  ·  인터페이스 ${machines.length}  ·  전송코드 ${total}  ·  ${date}`,
      machines.map(m => rowsOf(m, lookup)))
  } else {
    const used = new Set<string>()
    for (const m of machines) {
      const rows = rowsOf(m, lookup)
      fillSheet(wb.addWorksheet(sheetName(m.code || m.description, used)), `수집 데이터 — ${m.code || '(인터페이스 ID 없음)'}`,
        [specName, m.name?.trim(), m.description.trim(), `전송코드 ${m.items.length}`, date].filter(Boolean).join('  ·  '), [rows])
    }
  }

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${specName.replace(/[\\/:*?"<>|]/g, '_')}_수집데이터.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
